import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Avatar, 
  Chip,
  Button,
  Grid,
  useTheme,
  Divider,
  Menu,
  MenuItem,
  ListItemText,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  ClickAwayListener,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Alert,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Heart, 
  Activity, 
  Thermometer, 
  Droplet, 
  ScanHeart,
  Clock,
  Calendar,
  User,
  Search,
  X,
  Menu as MenuIcon
} from 'lucide-react';
import { HEALTH_GREEN } from '../../../Login/utils/mockData';
import { useNavigate } from 'react-router-dom';
import agthealthService from '../../../../services/agthealthService';
import { format } from 'date-fns';

const SummaryCard = styled(Box)(({ theme }) => ({
  borderRadius: 16,
  backgroundColor: theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF',
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  overflow: 'hidden',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'}`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column'
}));

const CardHeader = styled(Box)(({ theme, isMobile }) => ({
  padding: theme.spacing(isMobile ? 1.5 : 2, isMobile ? 2 : 3),
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  alignItems: isMobile ? 'flex-start' : 'center',
  justifyContent: 'space-between',
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
  gap: isMobile ? theme.spacing(1.5) : 0
}));

const CardContent = styled(Box)(({ theme, isMobile }) => ({
  padding: theme.spacing(isMobile ? 2 : 3),
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column'
}));

const MetricBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  borderRadius: 12,
  backgroundColor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7',
  height: '100%'
}));

const ActionButton = styled(Button)(({ theme, isMobile }) => ({
  borderRadius: 12,
  padding: theme.spacing(1, isMobile ? 1.5 : 2),
  textTransform: 'none',
  backgroundColor: HEALTH_GREEN,
  color: 'white',
  '&:hover': {
    backgroundColor: '#3d8b40',
  },
  fontWeight: 500,
  fontSize: isMobile ? '0.75rem' : 'inherit'
}));

const NoPatientBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  height: '100%',
  minHeight: 200,
  textAlign: 'center'
}));

// Componente de búsqueda estilizado similar a PatientList.js
const SearchField = styled(TextField)(({ theme, isMobile }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#f5f5f7',
    border: 'none',
    '& fieldset': { 
      border: 'none' 
    },
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? '#3C3C3E' : '#eaeaec',
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.mode === 'dark' ? '#3C3C3E' : '#fff',
      boxShadow: `0 0 0 2px ${HEALTH_GREEN}40`,
    }
  },
  width: isMobile ? '100%' : '250px',
  transition: 'width 0.2s',
  '&.expanded': {
    width: isMobile ? '100%' : '350px',
  }
}));

const SearchResultsContainer = styled(Paper)(({ theme, isMobile }) => ({
  position: 'absolute',
  zIndex: 1000,
  width: isMobile ? 'calc(100% - 32px)' : '350px',
  maxHeight: 300,
  overflowY: 'auto',
  marginTop: theme.spacing(1),
  borderRadius: 12,
  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
}));

const SearchResultItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(76, 175, 80, 0.08)',
  }
}));

const ActionButtonsContainer = styled(Box)(({ theme, isMobile }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexDirection: isMobile ? 'column' : 'row',
  width: isMobile ? '100%' : 'auto'
}));

const PatientInfoContainer = styled(Box)(({ theme, isMobile }) => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  alignItems: isMobile ? 'flex-start' : 'center',
  marginBottom: theme.spacing(isMobile ? 2 : 3),
  gap: theme.spacing(isMobile ? 2 : 0)
}));

const PatientMetaInfo = styled(Box)(({ theme, isMobile }) => ({
  marginLeft: isMobile ? 0 : theme.spacing(2),
  marginBottom: isMobile ? theme.spacing(1) : 0
}));

const PatientDatesInfo = styled(Box)(({ theme, isMobile }) => ({
  marginLeft: isMobile ? 0 : 'auto',
  textAlign: isMobile ? 'left' : 'right',
  width: isMobile ? '100%' : 'auto'
}));

const PatientSummary = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  
  // Media queries para responsividad
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPatient, setCurrentPatient] = useState(null);
  const [recentPatients, setRecentPatients] = useState([]);
  const [latestRecord, setLatestRecord] = useState(null);
  
  // Estados para búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  
  // Estado para el diálogo de cita
  const [appointmentDialog, setAppointmentDialog] = useState({
    open: false,
    patientId: null
  });
  
  // Estado para los datos del formulario de cita
  const [appointmentFormData, setAppointmentFormData] = useState({
    patientId: '',
    title: 'Consulta médica',
    date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    duration: 30,
    type: 'in-person',
    status: 'completed', // Cambiado de 'scheduled' a 'completed'
    location: '',
    notes: '',
    reason: ''
  });
  
  // Estado para el menú de acciones en móvil
  const [mobileActionsMenu, setMobileActionsMenu] = useState(null);
  
  // Estado para manejar el loading durante el guardado de cita
  const [savingAppointment, setSavingAppointment] = useState(false);
  
  // Función para formatear la fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'No disponible';
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch (e) {
      return 'Fecha inválida';
    }
  };
  
  // Función para calcular el tiempo transcurrido
  const getTimeElapsed = (dateString) => {
    if (!dateString) return 'No disponible';
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffMins < 1) return 'Hace unos segundos';
      if (diffMins < 60) return `Hace ${diffMins} minutos`;
      if (diffHours < 24) return `Hace ${diffHours} horas`;
      if (diffDays < 30) return `Hace ${diffDays} días`;
      return formatDate(dateString);
    } catch (e) {
      return 'Tiempo desconocido';
    }
  };
  
  // Cargar el paciente actual y la lista de pacientes recientes
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Obtener el último paciente visitado del localStorage
        const lastVisitedPatientId = localStorage.getItem('lastVisitedPatientId');
        
        // Obtener la lista de pacientes
        const patientsResponse = await agthealthService.getPatients();
        const patientsList = patientsResponse.data || [];
        
        // Ordenar por fecha de actualización más reciente
        const sortedPatients = [...patientsList].sort((a, b) => 
          new Date(b.updatedAt) - new Date(a.updatedAt)
        );
        
        // Guardar los pacientes recientes (máximo 5)
        setRecentPatients(sortedPatients.slice(0, 5));
        
        // Establecer el paciente actual
        if (lastVisitedPatientId && patientsList.some(p => p.patientId === lastVisitedPatientId)) {
          // Si hay un paciente visitado recientemente y existe en la lista
          const patient = patientsList.find(p => p.patientId === lastVisitedPatientId);
          setCurrentPatient(patient);
        } else if (sortedPatients.length > 0) {
          // Si no, usar el paciente más recientemente actualizado
          setCurrentPatient(sortedPatients[0]);
        }
        
        setError(null);
      } catch (err) {
        console.error("Error al cargar datos de pacientes:", err);
        setError("No se pudieron cargar los datos de pacientes");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Efecto para cargar el registro más reciente del paciente seleccionado
  useEffect(() => {
    const fetchLatestRecord = async () => {
      if (currentPatient) {
        try {
          // Obtener los registros del paciente seleccionado
          const response = await agthealthService.getRecordsByPatient(currentPatient.patientId);
          if (response.success && response.data && response.data.length > 0) {
            // Ordenar los registros por fecha (más reciente primero)
            const sortedRecords = [...response.data].sort((a, b) => 
              new Date(b.date) - new Date(a.date)
            );
            // Establecer el registro más reciente
            setLatestRecord(sortedRecords[0]);
          } else {
            setLatestRecord(null);
          }
        } catch (err) {
          console.error("Error al cargar el registro más reciente:", err);
          setLatestRecord(null);
        }
      }
    };
    
    fetchLatestRecord();
  }, [currentPatient]);
  
  // Efecto para la búsqueda de pacientes
  useEffect(() => {
    const searchPatients = async () => {
      if (!searchTerm.trim()) {
        setSearchResults([]);
        return;
      }
      
      try {
        setSearchLoading(true);
        const searchFilters = { search: searchTerm };
        const response = await agthealthService.searchPatients(searchFilters, 1, 10);
        
        if (response.success) {
          setSearchResults(response.data || []);
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        console.error("Error al buscar pacientes:", err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    };
    
    const handler = setTimeout(() => {
      searchPatients();
    }, 300);
    
    return () => clearTimeout(handler);
  }, [searchTerm]);
  
  // Manejar la selección de paciente
  const handlePatientSelect = (patient) => {
    setCurrentPatient(patient);
    localStorage.setItem('lastVisitedPatientId', patient.patientId);
    setSearchTerm('');
    setSearchResults([]);
    setSearchFocused(false);
  };
  
  // Navegar al detalle completo del paciente
  const handleViewFullRecord = () => {
    if (currentPatient) {
      navigate(`/patients/${currentPatient.patientId}`);
      handleCloseMobileMenu();
    }
  };
  
  // Función para manejar cambios en los campos del formulario de cita
  const handleAppointmentInputChange = (e) => {
    const { name, value } = e.target;
    setAppointmentFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Función para abrir el diálogo de registrar cita
  const handleRegisterAppointment = () => {
    if (currentPatient) {
      // Inicializar el formulario con los datos del paciente actual
      setAppointmentFormData({
        patientId: currentPatient.patientId,
        title: 'Consulta médica',
        date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        duration: 30,
        type: 'in-person',
        status: 'completed', // Cambiado de 'scheduled' a 'completed'
        location: '',
        notes: '',
        reason: ''
      });
      
      setAppointmentDialog({
        open: true,
        patientId: currentPatient.patientId
      });
      
      handleCloseMobileMenu();
    }
  };
  
  // Función para cerrar el diálogo de cita
  const handleCloseAppointmentDialog = () => {
    setAppointmentDialog({
      ...appointmentDialog,
      open: false
    });
    setError(null);
  };
  
  // Función para guardar la cita
  const handleSaveAppointment = async () => {
    try {
      setSavingAppointment(true);
      
      // Validar campos obligatorios
      if (!appointmentFormData.patientId || !appointmentFormData.date) {
        setError("Los campos de paciente y fecha son obligatorios");
        setSavingAppointment(false);
        return;
      }
      
      // Convertir la fecha al formato ISO
      const appointmentData = {
        ...appointmentFormData,
        date: new Date(appointmentFormData.date).toISOString()
      };
      
      // Llamar al servicio para crear la cita
      const response = await agthealthService.createAppointment(appointmentData);
      
      if (response.success || response.data) {
        // Cerrar el diálogo después de guardar exitosamente
        setAppointmentDialog({
          ...appointmentDialog,
          open: false
        });
        
        // Opcional: Mostrar un mensaje de éxito
        // setSuccessMessage("Cita registrada exitosamente");
      } else {
        setError("No se pudo guardar la cita");
      }
    } catch (err) {
      console.error("Error al guardar cita:", err);
      setError(err.response?.data?.message || "No se pudo guardar la cita");
    } finally {
      setSavingAppointment(false);
    }
  };
  
  // Función para generar las iniciales del paciente
  const getPatientInitials = (firstName, lastName) => {
    return firstName && lastName 
      ? `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
      : '?';
  };
  
  // Manejar cambios en el campo de búsqueda
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };
  
  // Limpiar búsqueda
  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
  };
  
  // Manejar focus del campo de búsqueda
  const handleSearchFocus = () => {
    setSearchFocused(true);
  };
  
  // Manejar click fuera del componente de búsqueda
  const handleClickAway = () => {
    setSearchFocused(false);
  };
  
  // Abrir menú de acciones en móvil
  const handleOpenMobileMenu = (event) => {
    setMobileActionsMenu(event.currentTarget);
  };
  
  // Cerrar menú de acciones en móvil
  const handleCloseMobileMenu = () => {
    setMobileActionsMenu(null);
  };
  
  // Renderizar cuando está cargando
  if (loading) {
    return (
      <SummaryCard>
        <CardHeader isMobile={isMobile}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Paciente Actual
          </Typography>
        </CardHeader>
        <CardContent isMobile={isMobile} sx={{ justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress sx={{ color: HEALTH_GREEN }} />
        </CardContent>
      </SummaryCard>
    );
  }
  
  // Renderizar cuando hay error
  if (error) {
    return (
      <SummaryCard>
        <CardHeader isMobile={isMobile}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Paciente Actual
          </Typography>
        </CardHeader>
        <CardContent isMobile={isMobile}>
          <NoPatientBox>
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button 
              variant="outlined"
              onClick={() => window.location.reload()}
              sx={{ borderRadius: 12 }}
            >
              Reintentar
            </Button>
          </NoPatientBox>
        </CardContent>
      </SummaryCard>
    );
  }
  
  // Renderizar cuando no hay pacientes
  if (!currentPatient) {
    return (
      <SummaryCard>
        <CardHeader isMobile={isMobile}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Paciente Actual
          </Typography>
        </CardHeader>
        <CardContent isMobile={isMobile}>
          <NoPatientBox>
            <User size={40} strokeWidth={1.5} style={{ marginBottom: 16, opacity: 0.5 }} />
            <Typography variant="body1" sx={{ mb: 2 }}>
              No hay pacientes registrados
            </Typography>
            <Button 
              variant="contained"
              onClick={() => navigate('/patients/new')}
              sx={{ 
                borderRadius: 12,
                backgroundColor: HEALTH_GREEN,
                '&:hover': { backgroundColor: '#3d8b40' }
              }}
            >
              Registrar Paciente
            </Button>
          </NoPatientBox>
        </CardContent>
      </SummaryCard>
    );
  }
  
  return (
    <>
      <SummaryCard>
        <CardHeader isMobile={isMobile}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            position: 'relative',
            width: isMobile ? '100%' : 'auto'
          }}>
            <ClickAwayListener onClickAway={handleClickAway}>
              <Box sx={{ width: isMobile ? '100%' : 'auto' }}>
                <SearchField
                  inputRef={searchInputRef}
                  variant="outlined"
                  placeholder="Buscar paciente"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  className={searchFocused ? 'expanded' : ''}
                  isMobile={isMobile}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={18} color={HEALTH_GREEN} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton 
                          onClick={handleClearSearch} 
                          size="small"
                          sx={{ 
                            color: '#999',
                            '&:hover': { color: '#666' }
                          }}
                        >
                          <X size={16} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                />
                {(searchFocused && searchResults.length > 0) && (
                  <SearchResultsContainer isMobile={isMobile}>
                    {searchResults.map((patient) => (
                      <SearchResultItem 
                        key={patient.patientId}
                        onClick={() => handlePatientSelect(patient)}
                      >
                        <Avatar 
                          sx={{ 
                            width: 32, 
                            height: 32, 
                            mr: 1.5,
                            bgcolor: HEALTH_GREEN
                          }}
                        >
                          {getPatientInitials(patient.firstName, patient.lastName)}
                        </Avatar>
                        <ListItemText 
                          primary={`${patient.firstName} ${patient.lastName}`}
                          secondary={
                            <Typography variant="body2" color="text.secondary">
                              {patient.identificationNumber} • 
                              {patient.age ? ` ${patient.age} años` : ' Edad N/A'} • 
                              {patient.gender === 'M' ? ' Masculino' : 
                               patient.gender === 'F' ? ' Femenino' : ' No especificado'}
                            </Typography>
                          }
                        />
                      </SearchResultItem>
                    ))}
                  </SearchResultsContainer>
                )}
                {(searchFocused && searchTerm && searchLoading) && (
                  <SearchResultsContainer isMobile={isMobile} sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} sx={{ color: HEALTH_GREEN }} />
                  </SearchResultsContainer>
                )}
                {(searchFocused && searchTerm && !searchLoading && searchResults.length === 0) && (
                  <SearchResultsContainer isMobile={isMobile} sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No se encontraron pacientes con ese criterio
                    </Typography>
                  </SearchResultsContainer>
                )}
              </Box>
            </ClickAwayListener>
            
            {currentPatient && !isMobile && (
              <Chip 
                label="Activo" 
                size="small" 
                sx={{ 
                  ml: 2, 
                  bgcolor: HEALTH_GREEN,
                  color: 'white',
                  fontWeight: 500,
                  fontSize: '0.75rem'
                }} 
              />
            )}
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', width: isMobile ? '100%' : 'auto' }}>
            {/* Versión móvil: menú de acciones */}
            {isMobile ? (
              <>
                <Button 
                  variant="contained"
                  startIcon={<MenuIcon size={16} />}
                  onClick={handleOpenMobileMenu}
                  fullWidth
                  sx={{ 
                    borderRadius: 12,
                    backgroundColor: HEALTH_GREEN,
                    '&:hover': { backgroundColor: '#3d8b40' }
                  }}
                >
                  Acciones
                </Button>
                <Menu
                  anchorEl={mobileActionsMenu}
                  open={Boolean(mobileActionsMenu)}
                  onClose={handleCloseMobileMenu}
                  PaperProps={{
                    sx: { 
                      borderRadius: 3,
                      minWidth: 200,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  <MenuItem onClick={handleViewFullRecord}>
                    Ver Registros
                  </MenuItem>
                  <MenuItem onClick={handleViewFullRecord}>
                    Ver Ficha Completa
                  </MenuItem>
                  <MenuItem onClick={handleRegisterAppointment}>
                    Registrar Cita
                  </MenuItem>
                </Menu>
              </>
            ) : (
              /* Versión desktop: botones normales */
              <ActionButtonsContainer>
                <ActionButton size="small" onClick={handleViewFullRecord} isMobile={isMobile}>
                  Ver Registros
                </ActionButton>
                <ActionButton size="small" onClick={handleViewFullRecord} isMobile={isMobile}>
                  Ver Ficha Completa
                </ActionButton>
                <ActionButton size="small" onClick={handleRegisterAppointment} isMobile={isMobile}>
                  Registrar Cita
                </ActionButton>
              </ActionButtonsContainer>
            )}
          </Box>
        </CardHeader>

        <CardContent isMobile={isMobile}>
          <PatientInfoContainer isMobile={isMobile}>
            <Avatar 
              sx={{ 
                width: isMobile ? 56 : 64, 
                height: isMobile ? 56 : 64, 
                bgcolor: HEALTH_GREEN,
                fontSize: isMobile ? '1.3rem' : '1.5rem'
              }}
            >
              {getPatientInitials(currentPatient.firstName, currentPatient.lastName)}
            </Avatar>
            
            <PatientMetaInfo isMobile={isMobile}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 600 }}>
                {`${currentPatient.firstName} ${currentPatient.lastName}`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {currentPatient.age ? `${currentPatient.age} años` : 'Edad no registrada'} • 
                {currentPatient.gender === 'male' ? ' Masculino' :
                 currentPatient.gender === 'female' ? ' Femenino' : 
                 ' No especificado'} • 
                ID: {currentPatient.patientId.substring(0, 8)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {currentPatient.diabetesType && (
                  <Chip 
                    label={currentPatient.diabetesType === 'type1' ? 'Diabetes Tipo 1' : 
                           currentPatient.diabetesType === 'type2' ? 'Diabetes Tipo 2' :
                           currentPatient.diabetesType === 'gestational' ? 'Diabetes Gestacional' :
                           currentPatient.diabetesType === 'prediabetes' ? 'Prediabetes' : 
                           currentPatient.diabetesType}
                    size="small" 
                    sx={{ 
                      bgcolor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7',
                      fontSize: '0.7rem'
                    }} 
                  />
                )}
                {currentPatient.chronicDiseases && currentPatient.chronicDiseases.slice(0, isMobile ? 1 : 2).map((disease, index) => (
                  <Chip 
                    key={index}
                    label={disease} 
                    size="small" 
                    sx={{ 
                      bgcolor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7',
                      fontSize: '0.7rem'
                    }} 
                  />
                ))}
                {currentPatient.chronicDiseases && currentPatient.chronicDiseases.length > (isMobile ? 1 : 2) && (
                  <Chip 
                    label={`+${currentPatient.chronicDiseases.length - (isMobile ? 1 : 2)} más`}
                    size="small" 
                    sx={{ 
                      bgcolor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7',
                      fontSize: '0.7rem'
                    }} 
                  />
                )}
              </Box>
            </PatientMetaInfo>
            
            <PatientDatesInfo isMobile={isMobile}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: isMobile ? 'flex-start' : 'flex-end', 
                mb: 1 
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                  Fecha de Registro:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatDate(currentPatient.createdAt)}
                </Typography>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: isMobile ? 'flex-start' : 'flex-end' 
              }}>
                <Calendar size={14} style={{ marginRight: 4 }} />
                <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                  Última Actualización:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatDate(currentPatient.updatedAt)}
                </Typography>
              </Box>
            </PatientDatesInfo>
          </PatientInfoContainer>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Signos Vitales
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6} md={4} lg={2.4}>
              <MetricBox>
                <Heart size={isMobile ? 16 : 20} color={HEALTH_GREEN} style={{ marginRight: 12 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Frecuencia Cardíaca
                  </Typography>
                  <Typography variant={isMobile ? "body2" : "subtitle2"} sx={{ fontWeight: 600 }}>
                    {latestRecord?.vitalSigns?.heartRate ? `${latestRecord.vitalSigns.heartRate} lpm` : 'No registrada'}
                  </Typography>
                </Box>
              </MetricBox>
            </Grid>
            
            <Grid item xs={6} md={4} lg={2.4}>
              <MetricBox>
                <Activity size={isMobile ? 16 : 20} color={HEALTH_GREEN} style={{ marginRight: 12 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Presión Arterial
                  </Typography>
                  <Typography variant={isMobile ? "body2" : "subtitle2"} sx={{ fontWeight: 600 }}>
                    {(latestRecord?.vitalSigns?.systolicBP && latestRecord?.vitalSigns?.diastolicBP) ? 
                      `${latestRecord.vitalSigns.systolicBP}/${latestRecord.vitalSigns.diastolicBP} mmHg` : 
                      'No registrada'}
                  </Typography>
                </Box>
              </MetricBox>
            </Grid>
            
            <Grid item xs={6} md={4} lg={2.4}>
              <MetricBox>
                <Droplet size={isMobile ? 16 : 20} color={HEALTH_GREEN} style={{ marginRight: 12 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Glucosa en Sangre
                  </Typography>
                  <Typography variant={isMobile ? "body2" : "subtitle2"} sx={{ fontWeight: 600 }}>
                    {latestRecord?.vitalSigns?.bloodGlucose ? `${latestRecord.vitalSigns.bloodGlucose} mg/dL` : 'No registrada'}
                  </Typography>
                </Box>
              </MetricBox>
            </Grid>
            
            <Grid item xs={6} md={4} lg={2.4}>
              <MetricBox>
                <Thermometer size={isMobile ? 16 : 20} color={HEALTH_GREEN} style={{ marginRight: 12 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Temperatura
                  </Typography>
                  <Typography variant={isMobile ? "body2" : "subtitle2"} sx={{ fontWeight: 600 }}>
                    {latestRecord?.vitalSigns?.temperature ? `${latestRecord.vitalSigns.temperature} °C` : 'No registrada'}
                  </Typography>
                </Box>
              </MetricBox>
            </Grid>
            
            <Grid item xs={6} md={4} lg={2.4}>
              <MetricBox>
                <ScanHeart size={isMobile ? 16 : 20} color={HEALTH_GREEN} style={{ marginRight: 12 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Saturación de Oxígeno
                  </Typography>
                  <Typography variant={isMobile ? "body2" : "subtitle2"} sx={{ fontWeight: 600 }}>
                    {latestRecord?.vitalSigns?.oxygenSaturation ? `${latestRecord.vitalSigns.oxygenSaturation}%` : 'No registrada'}
                  </Typography>
                </Box>
              </MetricBox>
            </Grid>
          </Grid>
        </CardContent>
      </SummaryCard>
      
      {/* Diálogo para registrar cita */}
      <Dialog 
        open={appointmentDialog.open} 
        onClose={handleCloseAppointmentDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            padding: isMobile ? 0 : 1
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Registrar Consulta Actual
        </DialogTitle>
        <DialogContent sx={{ pb: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {error}
            </Alert>
          )}
          
          <Alert severity="info" sx={{ mb: 3, mt: 1 }}>
            Este formulario es para registrar la consulta actual con el paciente seleccionado. Para agendar citas futuras, utilice la opción "Citas" en el menú lateral o en el widget correspondiente del dashboard.
          </Alert>
          
          {/* Contenido del formulario de cita */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="patient-label">Paciente</InputLabel>
                <Select
                  labelId="patient-label"
                  name="patientId"
                  value={appointmentFormData.patientId}
                  label="Paciente"
                  disabled
                >
                  <MenuItem value={currentPatient?.patientId || ''}>
                    {currentPatient ? `${currentPatient.firstName} ${currentPatient.lastName}` : ''}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Título"
                name="title"
                value={appointmentFormData.title}
                onChange={handleAppointmentInputChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha y Hora"
                name="date"
                type="datetime-local"
                value={appointmentFormData.date}
                onChange={handleAppointmentInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Cita</InputLabel>
                <Select
                  name="type"
                  value={appointmentFormData.type}
                  onChange={handleAppointmentInputChange}
                  label="Tipo de Cita"
                >
                  <MenuItem value="in-person">Presencial</MenuItem>
                  <MenuItem value="video">Videollamada</MenuItem>
                  <MenuItem value="phone">Telefónica</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Duración (minutos)"
                name="duration"
                type="number"
                value={appointmentFormData.duration}
                onChange={handleAppointmentInputChange}
                InputProps={{ inputProps: { min: 5, step: 5 } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  name="status"
                  value={appointmentFormData.status}
                  label="Estado"
                  disabled
                >
                  <MenuItem value="completed">Completada</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Ubicación"
                name="location"
                value={appointmentFormData.location}
                onChange={handleAppointmentInputChange}
                placeholder="Consultorio, dirección, etc."
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Motivo de la consulta"
                name="reason"
                value={appointmentFormData.reason}
                onChange={handleAppointmentInputChange}
                multiline
                rows={2}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas adicionales"
                name="notes"
                value={appointmentFormData.notes}
                onChange={handleAppointmentInputChange}
                multiline
                rows={2}
                placeholder="Observaciones, tratamiento recomendado, etc."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: isMobile ? 2 : 3, pb: isMobile ? 3 : 2 }}>
          <Button 
            onClick={handleCloseAppointmentDialog}
            disabled={savingAppointment}
            sx={{ borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Button 
            variant="contained"
            onClick={handleSaveAppointment}
            disabled={savingAppointment}
            sx={{ 
              borderRadius: 2,
              bgcolor: HEALTH_GREEN,
              '&:hover': { bgcolor: '#3d8b40' }
            }}
          >
            {savingAppointment ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PatientSummary;